import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ClickAwayListener } from "@mui/material";

import { useTopModalZIndex } from "@hooks/useModalZIndex";

// The trigger is a Button inside the anchor wrapper.
function triggerOf(anchorEl) {
    return anchorEl?.querySelector("button") || anchorEl || null;
}

/**
 * Anchored floating panel with the `_fragments` Popper look (surface, hairline,
 * overlay shadow, fade-in) but free-form content: the composer's option
 * panels lay out their own rows, captions and controls inside it.
 *
 * Portaled to <body> so it escapes the modal's clipping; positioned by hand
 * from the anchor's rect (flips when the preferred side has no room) and
 * repositioned whenever the panel changes size.
 */
export default function OptionPopper({
    open,
    anchorEl,
    onClose,
    placement = "top-start",
    width = 300,
    label,
    children,
}) {
    const zIndex = useTopModalZIndex();
    const [panelEl, setPanelEl] = useState(null);
    const [position, setPosition] = useState({ top: 0, left: 0, ready: false });

    // Latest values for listeners registered once per opening.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const panelRef = useRef(null);
    panelRef.current = panelEl;

    const portalNodeRef = useRef(null);
    if (typeof document !== "undefined" && portalNodeRef.current === null) {
        portalNodeRef.current = document.createElement("div");
        portalNodeRef.current.className = "pp-popper-portal";
    }

    useEffect(() => {
        const node = portalNodeRef.current;
        if (!node) return undefined;
        document.body.appendChild(node);
        return () => {
            if (node.parentNode) node.parentNode.removeChild(node);
        };
    }, []);

    // Escape closes the panel only. Registered in the capture phase on
    // document so it runs before the Modal's own Escape handling (a React
    // keydown on the modal root), which would otherwise close the whole
    // composer and discard the draft.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            e.preventDefault();
            onCloseRef.current?.();
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [open]);

    // Focus: into the panel once it is placed (the checked row, else the
    // first control); back to the trigger when it closes — unless the user
    // already moved focus elsewhere (e.g. clicked into the message field).
    useEffect(() => {
        if (!open || !position.ready || !panelEl) return;
        const target =
            panelEl.querySelector('[role="radio"][aria-checked="true"]') ||
            panelEl.querySelector('[role="radio"], input, button, [tabindex="0"]');
        target?.focus({ preventScroll: true });
    }, [open, position.ready, panelEl]);

    useEffect(() => {
        if (!open) return undefined;
        return () => {
            const active = document.activeElement;
            const lost = !active || active === document.body || panelRef.current?.contains(active);
            if (lost) triggerOf(anchorEl)?.focus({ preventScroll: true });
        };
    }, [open, anchorEl]);

    useLayoutEffect(() => {
        if (!open || !anchorEl || !panelEl) {
            setPosition((prev) => ({ ...prev, ready: false }));
            return undefined;
        }

        function compute() {
            const anchor = anchorEl.getBoundingClientRect();
            const panel = panelEl.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const gap = 8;
            const margin = 8;

            const [side, align] = String(placement).split("-");

            let top = side === "top" ? anchor.top - panel.height - gap : anchor.bottom + gap;
            let left = align === "end" ? anchor.right - panel.width : anchor.left;

            if (side === "top" && top < margin) {
                const below = anchor.bottom + gap;
                if (below + panel.height <= vh - margin) top = below;
            } else if (side === "bottom" && top + panel.height > vh - margin) {
                const above = anchor.top - panel.height - gap;
                if (above >= margin) top = above;
            }

            left = Math.max(margin, Math.min(left, vw - panel.width - margin));
            top = Math.max(margin, Math.min(top, vh - panel.height - margin));

            setPosition({ top, left, ready: true });
        }

        compute();

        // Content that unfolds inside the panel (the schedule's date inputs)
        // changes its height; keep it anchored above the trigger. compute()
        // only moves the panel, never resizes it, so this cannot loop.
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => compute()) : null;
        observer?.observe(panelEl);
        observer?.observe(anchorEl);

        window.addEventListener("scroll", compute, true);
        window.addEventListener("resize", compute);
        return () => {
            observer?.disconnect();
            window.removeEventListener("scroll", compute, true);
            window.removeEventListener("resize", compute);
        };
    }, [open, anchorEl, panelEl, placement]);

    if (!open || !anchorEl || !portalNodeRef.current) return null;

    // The trigger's own click toggles the panel; ignoring it here keeps a
    // click (or tap) on the open panel's button from closing and reopening it.
    function handleClickAway(event) {
        if (anchorEl.contains(event.target)) return;
        onCloseRef.current?.();
    }

    return createPortal(
        <div
            ref={setPanelEl}
            className="pp-popper"
            role="dialog"
            aria-label={label}
            style={{
                top: position.top,
                left: position.left,
                width: `min(${width}px, calc(100vw - 16px))`,
                zIndex,
                visibility: position.ready ? "visible" : "hidden",
            }}
        >
            <ClickAwayListener onClickAway={handleClickAway} touchEvent={false}>
                <div className="pp-popper__panel">{children}</div>
            </ClickAwayListener>
        </div>,
        portalNodeRef.current
    );
}
